import { CommonModule } from '@angular/common';
import { Component, inject, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { finalize, forkJoin, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { SharedModule } from 'src/app/shared/shared.module';
import { assertSuccessEnvelope } from 'src/app/core/http/api-envelope.operators';
import { resolveApiErrorMessage } from 'src/app/core/http/api-http-error';
import {
  RulesApiService,
  ActiveRulePackageInfo,
  RulePackageVersion,
  ComplianceLockInfo,
  CountryPackOption,
  PreviewResult,
} from 'src/app/core/services/rules-api.service';
import { TenantOnboardingApiService } from 'src/app/core/services/tenant-onboarding-api.service';
import { toCountryPackOptions } from '../country-packs/country-pack-options';
import { ActivePackagePanelComponent } from './active-package-panel/active-package-panel.component';
import { VersionHistoryTableComponent } from './version-history-table/version-history-table.component';
import { VersionDetailModalComponent } from './version-detail-modal/version-detail-modal.component';
import { ComplianceLocksPanelComponent } from './compliance-locks-panel/compliance-locks-panel.component';
import { RestoreConfirmModalComponent } from 'src/app/shared/components/restore-confirm-modal/restore-confirm-modal.component';

@Component({
  selector: 'app-rule-packs',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    SharedModule,
    ActivePackagePanelComponent,
    VersionHistoryTableComponent,
    VersionDetailModalComponent,
    ComplianceLocksPanelComponent,
    RestoreConfirmModalComponent,
  ],
  templateUrl: './rule-packs.component.html',
  styleUrl: './rule-packs.component.css',
})
export class RulePacksComponent implements OnInit {
  private readonly tenantApi = inject(TenantOnboardingApiService);
  private readonly rulesApi = inject(RulesApiService);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);

  tenants: { id: number; label: string }[] = [];
  products: { id: number; label: string }[] = [];
  countryPacks: CountryPackOption[] = [];

  selectedTenantId: number | null = null;
  selectedProductId: number | null = null;
  selectedCountry = '';

  pageLoading = false;
  pageError = '';

  // Action feedback
  actionBusy = false;
  actionMessage = '';
  actionError = '';

  // Active package panel
  active: ActiveRulePackageInfo | null = null;
  activeLoading = false;
  activeEmpty = false;
  activeRefreshing = false;

  // Version history
  versions: RulePackageVersion[] = [];
  versionsTotal = 0;
  versionsPage = 1;
  readonly versionsPageSize = 20;
  versionsLoading = false;
  rowBusyId: string | null = null;

  // Compliance locks
  locks: ComplianceLockInfo[] = [];
  locksLoading = false;
  get lockedKeys(): string[] {
    return this.locks.map((l) => l.parameter_key);
  }

  // Detail modal
  detailOpen = false;
  detailVersion: RulePackageVersion | null = null;
  preview: PreviewResult | null = null;
  validating = false;

  // Restore confirm
  restoreOpen = false;
  restoreTarget: RulePackageVersion | null = null;
  restoreBusy = false;

  // Publish confirm
  publishOpen = false;
  publishTarget: RulePackageVersion | null = null;
  publishBusy = false;

  get canAct(): boolean {
    return (
      this.selectedTenantId != null &&
      this.selectedTenantId > 0 &&
      this.selectedProductId != null &&
      this.selectedProductId > 0 &&
      !!this.selectedCountry
    );
  }

  ngOnInit(): void {
    this.pageLoading = true;
    this.pageError = '';
    forkJoin({
      tenants: this.tenantApi.listTenants({ page: 1, page_size: 500 }).pipe(assertSuccessEnvelope()),
      products: this.tenantApi.listProductTypesDropdown().pipe(assertSuccessEnvelope()),
      packs: this.rulesApi.listCountryPackRecords().pipe(assertSuccessEnvelope()),
    })
      .pipe(finalize(() => (this.pageLoading = false)))
      .subscribe({
        next: ({ tenants, products, packs }) => {
          this.tenants = (tenants.data?.result ?? []).map((t: any) => {
            const subdomainSuffix = t.tenant_subdomain ? ' · ' + t.tenant_subdomain : '';
            return { id: t.id, label: `${t.tenant_name ?? '—'} (#${t.id}${subdomainSuffix})` };
          });
          this.products = (products.data ?? []).map((p: any) => ({ id: p.id, label: `${p.name} (${p.slug})` }));
          this.countryPacks = toCountryPackOptions(packs.data ?? []);
          this.selectedCountry = '';

          const qp = this.route.snapshot.queryParamMap.get('tenantId');
          if (qp) {
            const n = Number(qp);
            if (!Number.isNaN(n) && n > 0 && this.tenants.some((t) => t.id === n)) {
              this.selectedTenantId = n;
            }
          }
          if (this.selectedTenantId == null && this.tenants.length === 1) {
            this.selectedTenantId = this.tenants[0].id;
          }
          if (this.products.length > 0 && this.selectedProductId == null) {
            this.selectedProductId = this.products[0].id;
          }
          const saved = this.route.snapshot.queryParamMap.get('saved');
          if (saved === 'published') this.actionMessage = 'Values published — they are now live for new sessions.';
          else if (saved === 'draft') this.actionMessage = 'Draft saved. Publish it from the version history to go live.';
          if (this.canAct) this.loadScope();
        },
        error: (e) => (this.pageError = resolveApiErrorMessage(e)),
      });
  }

  onTenantChange(): void {
    this.syncTenantQueryParam();
    this.clearActionFeedback();
    if (this.canAct) this.loadScope();
  }

  onScopeChange(): void {
    this.clearActionFeedback();
    this.versionsPage = 1;
    if (this.canAct) this.loadScope();
  }

  private loadScope(): void {
    this.loadActive();
    this.loadVersions(1);
    this.loadLocks();
  }

  private loadActive(): void {
    this.activeLoading = true;
    this.fetchActive(() => (this.activeLoading = false));
  }

  refreshActive(): void {
    this.activeRefreshing = true;
    this.fetchActive(() => (this.activeRefreshing = false));
  }

  private fetchActive(done: () => void): void {
    this.activeEmpty = false;
    this.rulesApi
      .getActivePackage(this.selectedTenantId!, this.selectedProductId!, this.selectedCountry)
      .pipe(
        assertSuccessEnvelope(),
        map((env) => ({ ok: true as const, data: env.data })),
        catchError(() => of({ ok: false as const, data: null })),
        finalize(done),
      )
      .subscribe((res) => {
        this.active = res.ok ? res.data : null;
        this.activeEmpty = !res.ok;
      });
  }

  loadVersions(page: number): void {
    this.versionsLoading = true;
    this.versionsPage = page;
    this.rulesApi
      .listVersions(this.selectedTenantId!, this.selectedProductId!, this.selectedCountry, page, this.versionsPageSize)
      .pipe(assertSuccessEnvelope(), finalize(() => (this.versionsLoading = false)))
      .subscribe({
        next: (env) => {
          this.versions = env.data.items ?? [];
          this.versionsTotal = env.data.total ?? 0;
        },
        error: () => {
          this.versions = [];
          this.versionsTotal = 0;
        },
      });
  }

  private loadLocks(): void {
    this.locksLoading = true;
    this.rulesApi
      .getComplianceLocks(this.selectedCountry)
      .pipe(assertSuccessEnvelope(), finalize(() => (this.locksLoading = false)))
      .subscribe({
        next: (env) => (this.locks = env.data ?? []),
        error: () => (this.locks = []),
      });
  }

  activate(): void {
    if (!this.canAct) return;
    this.actionBusy = true;
    this.clearActionFeedback();
    this.rulesApi
      .activateCountryPack(this.selectedTenantId!, this.selectedProductId!, this.selectedCountry)
      .pipe(assertSuccessEnvelope(), finalize(() => (this.actionBusy = false)))
      .subscribe({
        next: (env) => {
          this.actionMessage = env.message || 'Country pack activated.';
          this.loadActive();
          this.loadVersions(1);
        },
        error: (e) => (this.actionError = resolveApiErrorMessage(e)),
      });
  }

  // ── Edit (dedicated page) ────────────────────────────────────────────────────

  openEditor(): void {
    this.router.navigate(['/rule-packs/edit'], {
      queryParams: {
        tenantId: this.selectedTenantId,
        productId: this.selectedProductId,
        country: this.selectedCountry,
      },
    });
  }

  // ── Version detail + validate ──────────────────────────────────────────────

  openDetail(v: RulePackageVersion): void {
    this.detailVersion = v;
    this.preview = null;
    this.detailOpen = true;
  }

  closeDetail(): void {
    this.detailOpen = false;
    this.detailVersion = null;
    this.preview = null;
  }

  validateVersion(v: RulePackageVersion): void {
    this.validating = true;
    this.rulesApi
      .previewPackage(this.selectedTenantId!, this.selectedProductId!, this.selectedCountry, v.payload)
      .pipe(assertSuccessEnvelope(), finalize(() => (this.validating = false)))
      .subscribe({
        next: (env) => (this.preview = env.data),
        error: (e) => (this.actionError = resolveApiErrorMessage(e)),
      });
  }

  // ── Publish ────────────────────────────────────────────────────────────────

  askPublish(v: RulePackageVersion): void {
    this.publishTarget = v;
    this.publishOpen = true;
  }

  cancelPublish(): void {
    this.publishOpen = false;
    this.publishTarget = null;
  }

  confirmPublish(): void {
    if (!this.publishTarget) return;
    const target = this.publishTarget;
    this.publishBusy = true;
    this.rowBusyId = target.id;
    this.clearActionFeedback();
    this.rulesApi
      .publishVersion(target.id)
      .pipe(
        assertSuccessEnvelope(),
        finalize(() => {
          this.publishBusy = false;
          this.rowBusyId = null;
        }),
      )
      .subscribe({
        next: (env) => {
          this.actionMessage = env.message || `Version v${target.version_seq} published.`;
          this.publishOpen = false;
          this.publishTarget = null;
          this.loadActive();
          this.loadVersions(this.versionsPage);
        },
        error: (e) => {
          this.actionError = resolveApiErrorMessage(e);
          this.publishOpen = false;
          this.publishTarget = null;
        },
      });
  }

  // ── Restore ──────────────────────────────────────────────────────────────────

  askRestore(v: RulePackageVersion): void {
    this.restoreTarget = v;
    this.restoreOpen = true;
  }

  cancelRestore(): void {
    this.restoreOpen = false;
    this.restoreTarget = null;
  }

  confirmRestore(): void {
    if (!this.restoreTarget) return;
    const target = this.restoreTarget;
    this.restoreBusy = true;
    this.rowBusyId = target.id;
    this.clearActionFeedback();
    this.rulesApi
      .restoreVersion(target.id)
      .pipe(
        assertSuccessEnvelope(),
        finalize(() => {
          this.restoreBusy = false;
          this.rowBusyId = null;
        }),
      )
      .subscribe({
        next: (env) => {
          this.actionMessage = env.message || `Restored v${target.version_seq} as a new draft.`;
          this.restoreOpen = false;
          this.restoreTarget = null;
          this.loadVersions(1);
        },
        error: (e) => {
          this.actionError = resolveApiErrorMessage(e);
          this.restoreOpen = false;
          this.restoreTarget = null;
        },
      });
  }

  private syncTenantQueryParam(): void {
    const tid = this.selectedTenantId;
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { tenantId: tid != null && tid > 0 ? tid : null },
      queryParamsHandling: 'merge',
      replaceUrl: true,
    });
  }

  private clearActionFeedback(): void {
    this.actionMessage = '';
    this.actionError = '';
  }
}
